import React from "react";
import { useEffect } from "react";
import { Line } from "react-chartjs-2";
import {Card, CardHeader, Text} from "@fluentui/react-components";
import { webLightTheme } from '@fluentui/react-components';
import ChartStreaming from "@robloche/chartjs-plugin-streaming";
import { Chart as ChartJS, LineElement, LinearScale, PointElement} from 'chart.js';
import {ControlPanelNavigation} from "./control-panel-navigation.js";
import {HeartPulse20Regular} from "@fluentui/react-icons";
ChartJS.register(ChartStreaming, LineElement, PointElement, LinearScale);
ChartJS.defaults.font.family = "'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif";
ChartJS.defaults.color = webLightTheme.colorNeutralForeground1;

export const BiofeedbackChart = () => {
    const chartRef = React.useRef(null);
    const minHeartRateRef = React.useRef(Infinity);
    const maxHeartRateRef = React.useRef(-Infinity);
    const flameValueRef = React.useRef(null);

    // Normalize flame to values ranging from 80 to 110 to be visible alongside heart rate.
    const normalizeFlameValue = (flameValue) => {
        const min = Number.isFinite(minHeartRateRef.current) ? minHeartRateRef.current : 80;
        const max = Number.isFinite(maxHeartRateRef.current) ? maxHeartRateRef.current : 110;
        return Math.min(max, Math.max(min, flameValue * (max - min) + min));
    };

    const updateMinMaxHr = (value) => {
        const clampedValue = Math.min(150, Math.max(30, value));
        minHeartRateRef.current = Number.isFinite(minHeartRateRef.current)
            ? Math.min(minHeartRateRef.current, clampedValue)
            : clampedValue;
        maxHeartRateRef.current = Number.isFinite(maxHeartRateRef.current)
            ? Math.max(maxHeartRateRef.current, clampedValue)
            : clampedValue;
    };

    useEffect(() => {
        const onScenarioPropertyUpdated = (event) => {
            const chart = chartRef.current;
            if (!chart || !event?.detail) return;

            const { variableName, variableValue } = event.detail;
            const normalizedValue = Number(String(variableValue).replace(",", "."));
            if (!Number.isFinite(normalizedValue)) {console.error("Invalid value: " + variableValue); return;}
            const point = { x: Date.now(), y: normalizedValue };

            const heartRateChartDataset = chart.data.datasets[0].data;
            const flameChartDataset = chart.data.datasets[1].data;
            const filteredHeartRateChartDataset = chart.data.datasets[2].data;
            
            switch (variableName) {
                case "flame":
                    flameValueRef.current = normalizeFlameValue(point.y);
                    break;
                case "filteredHeartRate":
                    filteredHeartRateChartDataset.push(point);
                    break;
                case "heartRate":
                    heartRateChartDataset.push(point);
                    updateMinMaxHr(point.y);
                    break;
                default:
                    return;
            }
            
            flameValueRef.current != null && flameChartDataset.push({x: point.x, y: flameValueRef.current});
            chart.update("quiet");
        };
      
        window.addEventListener("scenarioPropertyUpdated", onScenarioPropertyUpdated);
        return () => window.removeEventListener("scenarioPropertyUpdated", onScenarioPropertyUpdated);
    }, []);

    const data = React.useMemo(() => ({
        datasets: [
            {
                label: "Heart rate [bpm - beats per minute]",
                borderColor: "red",
                fill: false,
                tension: 0.25,
                borderWidth: 1,
                pointStyle: 'circle',
                data: []
            },
            {
                label: "Intensity",
                borderColor: "blue",
                fill: false,
                stepped: true,
                borderWidth: 1,
                pointStyle: 'line',
                data: []
            },
            {
                label: "Filtered heart rate [bpm - beats per minute]",
                borderColor: "green",
                fill: false,
                tension: 0.25,
                borderWidth: 1,
                pointStyle: 'circle',
                data: []
            }
        ]
    }), []);

    const options = React.useMemo(() => ({
        plugins: {
            legend: {
                display: false
            },
            streaming: {
                frameRate: 15
            }
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                type: "realtime",
                realtime: {
                    delay: 1000,
                    duration: 120000
                }
            },
            y: {
                ticks: {
                    stepSize: 7,
                    callback: function (value, index, values) {
                        return value + 'BPM';
                    }
                }
            }
        }
    }), []);

    return (
        <>
            <div className='control-panel-layout'>
                <aside className="sidenav">
                    <ControlPanelNavigation/>
                </aside>
                <Card size='large' className="card" style={{ height: '88vh', margin: '12px' }}>
                    <CardHeader image={<HeartPulse20Regular />} header={<Text weight="semibold">Heart rate [bpm - beats per minute]</Text>} />
                    <Line ref={chartRef} data={data} options={options}/>
                </Card>
            </div>
        </>
    );
};